import { useSelector } from 'react-redux'
import type { RootState } from '../store'
import { productGateFor, ProductGate } from '../utils/productAvailability'

/**
 * The reusable product gate for React components.
 *
 *   const { isEnabled, isAvailable } = useProductEnabled()
 *   if (!isEnabled(MENU_ID.ALERTING_MONITORS)) return null
 *
 * Returns an object of predicates rather than taking a single id, because the
 * gate has to work in positions where a hook cannot be called per-id: inside
 * SideNavigation's menu.map() over N children, and inside the useEffect that
 * fronts MenuSelection's switch. One API for every call site.
 *
 * The returned gate is memoised on the location's coordinates, so it is
 * referentially stable across unrelated re-renders and safe in a deps array.
 *
 * Outside a redux Provider this throws, by design - see productGate() in
 * src/utils/productAvailability.ts for the snapshot escape hatch. Falling back
 * to the store singleton here would swallow genuine missing-Provider bugs and
 * silently stop reacting to location changes.
 */
export default function useProductEnabled(): ProductGate {
  // defaultLocation is `any` and is a NEW object on every setDefaultLocation
  // dispatch, so this selector's result is unstable by design. productGateFor()
  // re-keys on the lat/lng primitives, which are not.
  return productGateFor(
    useSelector((state: RootState) => state.user.defaultLocation)
  )
}

/** Just the region string, for callers that only need that. */
export function useCurrentRegion(): string {
  return useProductEnabled().region
}
